<script setup>
// Bloqueios de um profissional: férias, folgas, congressos (ver AGENDA.md).
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import agendaAPI from 'dashboard/api/agenda';
import { useAgendaData } from '../composables/useAgendaData';
import {
  addDays,
  datePart,
  formatNumericDate,
  todayIn,
} from '../helpers/agendaTime';

const LOOKAHEAD_DAYS = 400;

const { t } = useI18n();
const { setting } = useAgendaData();

const dialog = ref(null);
const professional = ref(null);
const blocks = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const form = ref({ starts_at: '', ends_at: '', reason: '' });

const canAdd = computed(
  () =>
    form.value.starts_at && form.value.ends_at && form.value.ends_at > form.value.starts_at
);

const describe = block => {
  const time = iso => iso.slice(11, 16);
  return `${formatNumericDate(datePart(block.starts_at))} ${time(block.starts_at)} – ${formatNumericDate(datePart(block.ends_at))} ${time(block.ends_at)}`;
};

const fetchBlocks = async () => {
  isLoading.value = true;
  try {
    const today = todayIn(setting.value.timezone);
    const { data } = await agendaAPI.getBlocks({
      professional_id: professional.value.id,
      from: today,
      to: addDays(today, LOOKAHEAD_DAYS),
    });
    blocks.value = data.payload;
  } catch {
    useAlert(t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const open = item => {
  professional.value = item;
  blocks.value = [];
  const today = todayIn(setting.value.timezone);
  form.value = {
    starts_at: `${today}T00:00`,
    ends_at: `${addDays(today, 1)}T00:00`,
    reason: '',
  };
  dialog.value?.open();
  fetchBlocks();
};

const add = async () => {
  if (!canAdd.value || isSaving.value) return;
  isSaving.value = true;
  try {
    await agendaAPI.createBlock({
      professional_id: professional.value.id,
      ...form.value,
    });
    useAlert(t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.ADDED'));
    form.value.reason = '';
    fetchBlocks();
  } catch (requestError) {
    useAlert(
      requestError?.response?.data?.message ||
        t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const remove = async block => {
  try {
    await agendaAPI.deleteBlock(block.id);
    blocks.value = blocks.value.filter(item => item.id !== block.id);
    useAlert(t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.REMOVED'));
  } catch {
    useAlert(t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="
      t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.TITLE', {
        name: professional?.name || '',
      })
    "
    :description="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.DESCRIPTION')"
    :show-confirm-button="false"
    :cancel-button-label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.CLOSE')"
    width="xl"
    position="top"
    @confirm="add"
  >
    <div class="flex flex-col gap-4" data-testid="agenda-blocks-dialog">
      <div v-if="isLoading" class="flex justify-center py-4">
        <Spinner />
      </div>
      <p v-else-if="!blocks.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-2">
        <li
          v-for="block in blocks"
          :key="block.id"
          class="flex items-center gap-3 px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak"
        >
          <span class="i-lucide-calendar-off size-4 text-n-slate-10 shrink-0" />
          <div class="flex flex-col flex-1 min-w-0">
            <span class="text-sm text-n-slate-12 tabular-nums">
              {{ describe(block) }}
            </span>
            <span v-if="block.reason" class="text-label-small text-n-slate-11">
              {{ block.reason }}
            </span>
          </div>
          <Button
            type="button"
            :label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.REMOVE')"
            variant="ghost"
            color="ruby"
            size="xs"
            @click="remove(block)"
          />
        </li>
      </ul>

      <div class="grid gap-3 pt-4 border-t border-n-weak sm:grid-cols-2">
        <Input
          v-model="form.starts_at"
          type="datetime-local"
          :label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.START')"
        />
        <Input
          v-model="form.ends_at"
          type="datetime-local"
          :label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.END')"
        />
        <Input
          v-model="form.reason"
          class="sm:col-span-2"
          :label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.REASON')"
          :placeholder="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.REASON_PLACEHOLDER')"
        />
        <div class="flex justify-end sm:col-span-2">
          <Button
            type="submit"
            icon="i-lucide-plus"
            :label="t('AGENDA.PROFESSIONALS.BLOCKS_DIALOG.ADD')"
            size="sm"
            :disabled="!canAdd"
            :is-loading="isSaving"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
